import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatStepper } from '@angular/material/stepper';
import gql from 'graphql-tag';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AppSyncService } from 'src/app/core/appsync.service';
import { emailVerification } from 'src/app/shared/shared-validators/email-validator';
import { addStaff, updateStaffType } from 'src/graphql/mutations';
import {
  AddStaffMutationVariables,
  Doctor,
  UpdateStaffTypeMutationVariables
} from './../../../../../API';
import { ClinicSetupService } from './../../clinic-setup.service';

interface StaffActionData {
  staff?: Doctor;
  action: 'update' | 'add';
}

@Component({
  selector: 'csi-staff-actions-modal',
  templateUrl: './staff-actions-modal.component.html',
  styleUrls: ['./staff-actions-modal.component.scss']
})
export class StaffActionsModalComponent implements OnInit {
  @ViewChild('stepper', { static: false })
  stepper: MatStepper;

  public emailControl = new FormControl(null, [Validators.required, Validators.email]);

  public staffFormGroup = new FormGroup({
    email: this.emailControl,
    verifyEmail: new FormControl(null, [emailVerification(this.emailControl), Validators.required]),
    firstName: new FormControl(null, Validators.required),
    lastName: new FormControl(null, Validators.required),
    staffType: new FormControl(null, Validators.required)
  });

  public confirmControl = new FormControl(false, Validators.requiredTrue);

  public errorMessage: string;

  public matSpinnerOptions: MatProgressButtonOptions = {
    active: false,
    text: this.data.action === 'add' ? 'Adding…' : 'Updating…',
    raised: true,
    buttonColor: 'primary',
    spinnerSize: 19,
    mode: 'indeterminate',
    fullWidth: false
  };

  public matSpinnerFinishOptions: MatProgressButtonOptions = {
    active: false,
    text: this.data.action === 'add' ? 'Create Account' : 'Update Account',
    raised: true,
    buttonColor: 'primary',
    spinnerSize: 19,
    mode: 'indeterminate',
    fullWidth: false
  };

  constructor(
    public appSyncService: AppSyncService,
    @Inject(MAT_DIALOG_DATA) public data: StaffActionData,
    public dialogRef: MatDialogRef<StaffActionsModalComponent>,
    public clinicSetupService: ClinicSetupService
  ) {
    if (data.staff) {
      this.staffFormGroup.disable();
      this.staffFormGroup.patchValue({
        email: data.staff.email,
        firstName: data.staff.firstName,
        lastName: data.staff.lastName,
        staffType: data.staff.staffType
      });
      this.staffFormGroup.get('staffType').enable();
    }
  }

  ngOnInit() {}

  nextStep() {
    this.staffFormGroup.markAllAsTouched();
    if (!this.staffFormGroup.valid) return;
    this.errorMessage = '';
    this.stepper.next();
  }

  finish() {
    this.confirmControl.markAsTouched();
    if (!this.confirmControl.valid) return;
    this.errorMessage = '';
    this.matSpinnerFinishOptions.active = true;
    const action$ = this.data.action === 'add' ? this.addStaff() : this.updateStaffType();
    action$.subscribe(
      (res: any) => {
        if (res.errors && res.errors.length > 0) {
          this.errorMessage = res.errors[0].message;
          this.matSpinnerFinishOptions.active = false;
        } else {
          this.dialogRef.close(true);
        }
      },
      err => {
        this.errorMessage = err.message || 'Unknown error';
        this.matSpinnerFinishOptions.active = false;
      }
    );
  }

  addStaff(): Observable<any> {
    const input: AddStaffMutationVariables = {
      ...this.staffFormGroup.getRawValue(),
      clinicId: this.clinicSetupService.clinicId,
      clinicSetupId: this.clinicSetupService.clinicSetupId
    };
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.mutate({
          mutation: gql(addStaff),
          variables: input,
          errorPolicy: 'all'
        })
      )
    );
  }

  updateStaffType(): Observable<any> {
    const input: UpdateStaffTypeMutationVariables = {
      id: this.data.staff.id,
      clinicId: this.clinicSetupService.clinicId,
      clinicSetupId: this.clinicSetupService.clinicSetupId,
      staffType: this.staffFormGroup.get('staffType').value
    };
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.mutate({
          mutation: gql(updateStaffType),
          variables: input,
          errorPolicy: 'all'
        })
      )
    );
  }
}
